import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../store/AuthContext";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  MenuItem,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
  InputAdornment,
  CircularProgress,
  Grid
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getClasses, getSubjects } from "../api/classSubjectApi";
import {
  CloudUpload,
  Title,
  Description,
  Link as LinkIcon,
  School,
  Subject as SubjectIcon,
  ArrowBack
} from "@mui/icons-material";
import { getTopics } from "../api/topicsApi";
import TopicIcon from "@mui/icons-material/Topic";

export default function TeacherUploadVideo() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    class_id: "",
    subject_id: "",
    topic_id: "",
    file_path: "",
    admin_id: user.admin_id,
    teacher_id: user.id
  });

  useEffect(() => {
    loadClassAndSubject();
    loadTopics();
  }, []);

  const loadTopics = async () => {
    try {
      const res = await axios.get(
        "https://lms.childinfo.in/api/topics.php",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTopics(res.data || []);
    } catch (err) {
      console.error("Error loading topics", err);
    }
  };

  const loadClassAndSubject = async () => {
    try {
      const classRes = await getClasses(token);
      const subjectRes = await getSubjects(token);

      setClasses(classRes.data);
      setSubjects(subjectRes.data);
    } catch (err) {
      console.error("Error loading class/subject", err);
      setError("Failed to load classes and subjects");
    }
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const validateForm = () => {
    if (!form.title.trim()) {
      setError("Title is required");
      return false;
    }
    if (!form.class_id) {
      setError("Please select a class");
      return false;
    }
    if (!form.subject_id) {
      setError("Please select a subject");
      return false;
    }
    if (!form.topic_id) {
      setError("Please select a topic");
      return false;
    }
    if (!form.file_path.trim()) {
      setError("Video URL is required");
      return false;
    }
    if (!form.file_path.includes("youtube.com") && !form.file_path.includes("youtu.be")) {
      setError("Please enter a valid YouTube URL");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      await axios.post(
        "https://lms.childinfo.in/api/videos.php",
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Video uploaded successfully!");
      navigate("/teacher");
    } catch (err) {
      console.error(err);
      setError("Failed to upload video. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeThumbnail = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
  };

  const thumbnailUrl = getYouTubeThumbnail(form.file_path);

  const filteredTopics = topics.filter(
    t => String(t.subject_id) === String(form.subject_id) && t.status === "Active"
  );


  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 2, sm: 3, md: 4 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          alignItems: { xs: "flex-start", sm: "center" }, 
          justifyContent: "space-between", 
          gap: 2 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ 
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1
              }}
            >
              <CloudUpload sx={{ fontSize: { xs: 32, sm: 40 } }} />
              Upload Video
            </Typography>
            <Typography variant={isMobile ? "body2" : "h6"} sx={{ opacity: 0.9 }}>
              Add new educational content for your students
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            size={isMobile ? "medium" : "large"}
            startIcon={<ArrowBack />}
            onClick={() => navigate("/teacher")}
            sx={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              py: 1,
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
              minWidth: { xs: "100%", sm: "auto" }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}>
                Video Details
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Video Title"
                name="title"
                margin="normal"
                value={form.title}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Title color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
                placeholder="Enter video title"
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                margin="normal"
                multiline
                rows={3}
                value={form.description}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
                placeholder="Enter video description"
              />

              {/* CLASS DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Class"
                name="class_id"
                margin="normal"
                value={form.class_id}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <School color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">
                  <em>Select a class</em>
                </MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.class}
                  </MenuItem>
                ))}
              </TextField>

              {/* SUBJECT DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Subject"
                name="subject_id"
                margin="normal"
                value={form.subject_id}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SubjectIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">
                  <em>Select a subject</em>
                </MenuItem>
                {subjects.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.subject}
                  </MenuItem>
                ))}
              </TextField>

              {/* TOPIC DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Topic"
                name="topic_id"
                margin="normal"
                value={form.topic_id}
                onChange={handleChange}
                disabled={!form.subject_id}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TopicIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">
                  <em>{form.subject_id ? "Select a topic" : "Select subject first"}</em>
                </MenuItem>

                {filteredTopics.map((topic) => (
                  <MenuItem key={topic.id} value={topic.id}>
                    {topic.topic_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="YouTube Video URL"
                name="file_path"
                margin="normal"
                value={form.file_path}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
                placeholder="https://www.youtube.com/watch?v=..."
                helperText="Enter a valid YouTube URL"
              />

              <Button
                variant="contained"
                fullWidth
                size={isMobile ? "medium" : "large"}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                onClick={handleUpload}
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  background: "linear-gradient(45deg, #4CAF50, #45a049)",
                  fontWeight: "bold",
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  "&:hover": {
                    background: "linear-gradient(45deg, #45a049, #3d8b40)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? "Uploading..." : "Upload Video"}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}>
                Video Preview
              </Typography>

              {thumbnailUrl ? (
                <Box>
                  {/* Thumbnail Preview */}
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 3,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      component="img"
                      src={thumbnailUrl}
                      alt="Video thumbnail"
                      sx={{
                        width: "100%",
                        height: { xs: 180, sm: 200, md: 240 },
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>

                  {/* Preview Details */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {form.title || "No title entered"}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                      {form.description || "No description entered"}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {form.class_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2">
                            Class: {classes.find(c => c.id === form.class_id)?.class || "N/A"}
                          </Typography>
                        </Box>
                      )}
                      
                      {form.subject_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <SubjectIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Subject: {subjects.find(s => s.id === form.subject_id)?.subject || "N/A"}
                          </Typography>
                        </Box>
                      )}

                      {form.topic_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TopicIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Topic: {topics.find(t => t.id === form.topic_id)?.topic_name || "N/A"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: { xs: 180, sm: 200, md: 240 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Box textAlign="center">
                    <LinkIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Enter a YouTube URL to see preview
                    </Typography>
                  </Box>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Enter a valid YouTube URL to preview the thumbnail.
                  Your video should be accessible on YouTube.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}